import { useState, useEffect } from 'react';
import { useBackendSettings } from './useBackendSettings';

const SESSION_DISMISSED_KEY = 'scribelate_podcast_discovery_session_dismissed';

interface UsePodcastDiscoveryTutorialOptions {
  projectCount: number;
  episodeCount: number;
}

export const usePodcastDiscoveryTutorial = (options: UsePodcastDiscoveryTutorialOptions) => {
  const { projectCount, episodeCount } = options;
  const [showTutorial, setShowTutorial] = useState(false);
  const { settings, saveSettings, isLoading: isLoadingSettings } = useBackendSettings();

  useEffect(() => {
    // Wait for settings to load from backend before deciding whether to show tutorial
    if (isLoadingSettings) {
      return;
    }

    // Check if user has permanently completed this tutorial (from backend)
    const hasCompletedTutorial = settings?.podcast_discovery_completed === 'true';
    
    // Check if user dismissed it this session
    const sessionDismissed = sessionStorage.getItem(SESSION_DISMISSED_KEY);
    
    console.log('🎓 Tutorial Hook:', {
      projectCount,
      episodeCount,
      hasCompletedTutorial: !!hasCompletedTutorial,
      sessionDismissed: !!sessionDismissed,
      willShow: projectCount === 1 && episodeCount === 0 && !hasCompletedTutorial && !sessionDismissed
    });
    
    // Show tutorial if:
    // 1. User has exactly 1 project (just created their first one)
    // 2. User has 0 episodes (hasn't added any podcasts yet)
    // 3. Haven't completed tutorial permanently
    // 4. Haven't dismissed it this session
    if (projectCount === 1 && episodeCount === 0 && !hasCompletedTutorial && !sessionDismissed) {
      setShowTutorial(true);
    } else {
      setShowTutorial(false);
    }
  }, [projectCount, episodeCount, settings, isLoadingSettings]);

  const completeTutorial = () => {
    saveSettings({ podcast_discovery_completed: 'true' });
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
    setShowTutorial(false);
  };

  const skipTutorial = () => {
    // Only set session storage
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
    setShowTutorial(false);
  };

  const resetTutorial = () => {
    saveSettings({ podcast_discovery_completed: 'false' });
    sessionStorage.removeItem(SESSION_DISMISSED_KEY);
    setShowTutorial(true);
  };

  return {
    showTutorial,
    isLoading: isLoadingSettings,
    completeTutorial,
    skipTutorial,
    resetTutorial,
  };
};
